import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MessageCircle, CheckCircle2, ChevronDown } from "lucide-react";
import { IMAGES, waLink } from "@/lib/constants";
import { scrollToSection } from "@/lib/scroll";

const LINES = [
  { text: "SEU CARRO MERECE", accent: false },
  { text: "UMA REVISÃO", accent: false },
  { text: "DE VERDADE.", accent: true },
];

const CHECKS = ["Atendimento especializado", "Diagnóstico completo", "Transparência no serviço"];
const EASE = [0.16, 1, 0.3, 1];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  return (
    <section id="inicio" ref={ref} className="relative min-h-screen flex items-center overflow-hidden grain">
      <motion.div style={{ y: imgY }} className="absolute inset-0 scale-110">
        <img
          src={IMAGES.hero}
          alt="Fachada da MW Auto Center em Balneário Camboriú"
          className="h-full w-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/80 to-ink/30" />
      <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-ink/70" />

      <div className="relative z-10 px-4 sm:px-6 lg:px-10 max-w-[1400px] mx-auto w-full pt-36 pb-24">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0, duration: 0.8, ease: EASE }}
          className="inline-block font-mono text-[11px] uppercase tracking-[0.3em] text-volt border border-volt/40 px-4 py-2 mb-8"
        >
          ● Oficina Mecânica — Balneário Camboriú · SC
        </motion.p>

        <h1
          data-testid="hero-title"
          className="font-display font-black uppercase tracking-tight leading-[0.92] text-5xl sm:text-7xl lg:text-8xl text-white"
        >
          {LINES.map((line, i) => (
            <span key={line.text} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${line.accent ? "text-signal" : ""}`}
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{ delay: 1.15 + i * 0.14, duration: 1, ease: EASE }}
              >
                {line.text}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          data-testid="hero-subtitle"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.7, duration: 0.9, ease: EASE }}
          className="mt-6 max-w-xl text-sm sm:text-base text-gray-300 leading-relaxed"
        >
          Balanceamento, geometria, troca de óleo, freios e manutenção completa
          para você dirigir com mais segurança e tranquilidade.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.9, duration: 0.9, ease: EASE }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <a
            data-testid="hero-whatsapp-button"
            href={waLink()}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-cut inline-flex items-center gap-2 px-8 py-4 bg-signal hover:bg-signalhover text-white font-display font-bold uppercase tracking-wide text-lg transition-colors duration-300"
          >
            <MessageCircle className="h-5 w-5" />
            Agendar pelo WhatsApp
          </a>
          <button
            data-testid="hero-services-button"
            onClick={() => scrollToSection("#servicos")}
            className="btn-cut px-8 py-4 border border-white/25 text-white hover:border-volt hover:text-volt font-display font-bold uppercase tracking-wide text-lg transition-colors duration-300"
          >
            Conheça Nossos Serviços
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2.2, duration: 1 }}
          className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-3"
        >
          {CHECKS.map((c) => (
            <span key={c} className="flex items-center gap-2 text-sm text-gray-300">
              <CheckCircle2 className="h-4 w-4 text-signal" />
              {c}
            </span>
          ))}
        </motion.div>
      </div>

      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={() => scrollToSection("#servicos")}
        aria-label="Rolar para serviços"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.5 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 text-gray-400 hover:text-volt transition-colors"
      >
        <ChevronDown className="h-6 w-6 animate-bounce" />
      </motion.button>
    </section>
  );
};
